import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaShieldAlt, FaPlus, FaTrash, FaSave, FaSync, FaSearch, FaUserTag,
  FaCheckCircle, FaTimesCircle, FaChevronRight, FaChevronDown, FaTimes,
  FaUserCog, FaUsers, FaLaptop, FaSitemap
} from "react-icons/fa";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";

const DEFAULT_ROLE_NAME = "Club Member";

// roles.php / paths.php / role_paths.php / role_assign.php are the real,
// working, DB-backed API (roles + role_paths + paths tables already exist
// with real seed data) -- this page previously called
// roles_from_memberbasic.php (an empty 0-byte file) and
// role_paths_by_name.php (didn't exist at all), which is why roles never
// loaded and "Save access" silently did nothing. All four endpoints below
// respond as {success, data} via Backend/api_helpers.php's ok()/err().
const API = {
  roles:      "https://dev.sliitleo.org/Backend/roles.php",
  paths:      "https://dev.sliitleo.org/Backend/paths.php",
  rolePaths:  "https://dev.sliitleo.org/Backend/role_paths.php",
  roleAssign: "https://dev.sliitleo.org/Backend/role_assign.php",
};

async function fetchJson(url, options) {
  const res = await fetch(url, options);
  const body = await res.json().catch(() => null);
  if (!res.ok || !body || body.success === false) {
    throw new Error(body?.error || `Request failed (${res.status})`);
  }
  return body.data;
}

const FALLBACK_PATHS = [
  { key: "home",            label: "Home",              route: "/home" },
  { key: "profile",         label: "My Profile",        route: "/profile" },
  { key: "education",       label: "Leo Education",     route: "/education" },
  { key: "tickets",         label: "My Tickets",        route: "/tickets" },
  { key: "calendar",        label: "Club Calendar",     route: "/club-calander" },
  { key: "admin",           label: "Admin Console",     route: "/admin" },
  { key: "projects",        label: "Club Projects",     route: "/club-projects" },
  { key: "dashboard",       label: "Club Dashboard",    route: "/club-dashboard" },
  { key: "reporting",       label: "Reporting Hub",     route: "/reporting-hub" },
  { key: "installation",    label: "Installation",      route: "/installation" },
  { key: "education_admin", label: "Education Admin",   route: "/education-statistics" },
  { key: "attendance",      label: "Attendance",        route: "/attendance" },
];

/* Tiny toast without extra libs */
const Toast = ({ type = "ok", msg }) => (
  <div className={type === "ok" ? "success-banner" : "error-banner"} style={{ boxShadow: '0 20px 50px -20px rgba(0,0,0,.7)' }}>
    {type === "ok" ? <FaCheckCircle size={14} /> : <FaTimesCircle size={14} />}
    <span>{msg}</span>
  </div>
);

export default function RolePathManagement() {
  const [roles, setRoles] = useState([]);                 // [{id, name, is_default}]
  const [paths, setPaths] = useState([]);
  const [activeRoleId, setActiveRoleId] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [newRoleName, setNewRoleName] = useState("");
  const [addingRole, setAddingRole] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState({ emailOrId: "", roleId: "" });
  const [toast, setToast] = useState(null);
  const [rolePanelOpen, setRolePanelOpen] = useState(true);

  const activeRole = roles.find(r => r.id === activeRoleId) || null;

  const showToast = useCallback((msg, type="ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2000);
  }, []);

  const loadRoles = useCallback(async ({ preserveActive = true } = {}) => {
    const data = await fetchJson(API.roles);
    setRoles(data || []);
    if (!preserveActive || activeRoleId == null || !(data || []).some(r => r.id === activeRoleId)) {
      const preferred = (data || []).find(r => Number(r.is_default) === 1) || (data || []).find(r => r.name.toLowerCase() === DEFAULT_ROLE_NAME.toLowerCase());
      setActiveRoleId(preferred?.id ?? data?.[0]?.id ?? null);
    }
    return data;
  }, [activeRoleId]);

  // bootstrap
  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        const [rolesData, pathsData] = await Promise.all([
          fetchJson(API.roles),
          fetchJson(API.paths).catch(() => FALLBACK_PATHS),
        ]);
        setRoles(rolesData || []);
        setPaths((pathsData && pathsData.length ? pathsData : FALLBACK_PATHS));

        const preferred = (rolesData || []).find(r => Number(r.is_default) === 1) || (rolesData || []).find(r => r.name.toLowerCase() === DEFAULT_ROLE_NAME.toLowerCase());
        setActiveRoleId(preferred?.id ?? rolesData?.[0]?.id ?? null);
      } catch {
        setPaths(FALLBACK_PATHS);
        setRoles([]);
        setActiveRoleId(null);
        showToast("Could not load roles/paths from the server", "err");
      } finally {
        setLoading(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // when active role changes, load its path keys
  useEffect(() => {
    if (activeRoleId == null) {
      setSelectedKeys(new Set());
      return;
    }
    (async () => {
      try {
        const u = new URL(API.rolePaths);
        u.searchParams.set("roleId", String(activeRoleId));
        const data = await fetchJson(u.toString());
        setSelectedKeys(new Set((data || []).map(x => x.key)));
      } catch {
        setSelectedKeys(new Set());
      }
    })();
  }, [activeRoleId]);

  const filteredPaths = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return paths;
    return paths.filter(p =>
      p.label.toLowerCase().includes(q) ||
      p.route.toLowerCase().includes(q) ||
      p.key.toLowerCase().includes(q)
    );
  }, [paths, search]);

  const toggleKey = (key) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const selectAll = () => setSelectedKeys(new Set(paths.map(p => p.key)));
  const clearAll  = () => setSelectedKeys(new Set());

  const saveRolePaths = async () => {
    if (activeRoleId == null) return;
    setSaving(true);
    try {
      await fetchJson(API.rolePaths, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roleId: activeRoleId, keys: [...selectedKeys] })
      });
      showToast("Access map saved");
    } catch (e) {
      showToast(e.message || "Failed to save", "err");
    } finally {
      setSaving(false);
    }
  };

  // Assign role to user (uses existing endpoint that updates MemberBasic.Role)
  const assignRole = async () => {
    const { emailOrId, roleId } = assignTarget;
    if (!emailOrId || !roleId) return;
    try {
      await fetchJson(API.roleAssign, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailOrId, roleId: Number(roleId) })
      });
      setAssignOpen(false);
      setAssignTarget({ emailOrId: "", roleId: "" });
      showToast("Role assigned to user");
    } catch (e) {
      showToast(e.message || "Assign failed", "err");
    }
  };

  const addRole = async () => {
    const name = newRoleName.trim();
    if (!name) return;
    setAddingRole(true);
    try {
      const created = await fetchJson(API.roles, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      setNewRoleName("");
      await loadRoles({ preserveActive: false });
      if (created?.id != null) setActiveRoleId(created.id);
      showToast(`Role "${name}" created`);
    } catch (e) {
      showToast(e.message || "Failed to create role", "err");
    } finally {
      setAddingRole(false);
    }
  };

  return (
    <div className="min-h-screen text-white" style={{ background: "var(--bg-base, #070a12)" }}>
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-0 lg:ml-64">
          <Topbar user={JSON.parse(localStorage.getItem("user")||"{}")} />

          {/* Inline marginTop (not the `mt-14` class) because shell.css's own
              `.content{margin:0 auto}` rule has higher specificity than a
              single Tailwind utility class and was silently overriding it,
              leaving this page's header hidden behind the fixed topbar. */}
          <div className="content" style={{ marginTop: 56 }}>
            {/* Header */}
            <div className="page-head">
              <div className="flex items-center gap-3">
                <div
                  style={{
                    width: 44, height: 44, borderRadius: 'var(--radius-md)', flexShrink: 0,
                    background: 'rgba(225,29,46,.14)', color: 'var(--accent)',
                    display: 'grid', placeItems: 'center',
                  }}
                >
                  <FaShieldAlt size={18} />
                </div>
                <div>
                  <h1>Role &amp; Path Management</h1>
                  <p className="page-desc">
                    Manage which pages each role can access. Assigning a role updates <b style={{ color: 'var(--text-1)' }}>MemberBasic.Role</b>.
                  </p>
                </div>
              </div>

              <div className="filter-row" style={{ flexWrap: 'wrap' }}>
                <button onClick={() => setAssignOpen(true)} className="btn btn-ghost">
                  <FaUserTag size={13} /> Assign role to user
                </button>
                <button onClick={() => window.location.reload()} className="btn btn-ghost">
                  <FaSync size={13} /> Refresh
                </button>
              </div>
            </div>

            {/* Navigation -- same admin quick-nav used by Session/Device Management */}
            <div className="filter-row" style={{ flexWrap: 'wrap', marginBottom: 'var(--sp-5)' }}>
              <Link to="/admin" className="chip">
                <FaUserCog size={12} /> Role Management
              </Link>
              <Link to="/session-management" className="chip">
                <FaUsers size={12} /> Session Management
              </Link>
              <Link to="/device-management" className="chip">
                <FaLaptop size={12} /> Device Management
              </Link>
              <Link to="/path-management" className="chip active">
                <FaSitemap size={12} /> Role Path Management
              </Link>
            </div>

            {/* Content */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

              {/* Roles panel */}
              <div className="lg:col-span-4">
                <div className="panel">
                  <button
                    onClick={() => setRolePanelOpen(v => !v)}
                    className="section-head"
                    style={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)' }}
                    aria-expanded={rolePanelOpen}
                  >
                    <h2>Roles</h2>
                    {rolePanelOpen ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                  </button>

                  {rolePanelOpen && (
                    <div style={{ padding: 'var(--sp-4)' }}>
                      <div style={{ display: 'flex', gap: 8, marginBottom: 'var(--sp-3)' }}>
                        <input
                          type="text"
                          value={newRoleName}
                          onChange={(e) => setNewRoleName(e.target.value)}
                          placeholder="New role name"
                          aria-label="New role name"
                          onKeyDown={(e) => { if (e.key === 'Enter') addRole(); }}
                        />
                        <button onClick={addRole} disabled={addingRole || !newRoleName.trim()} className="btn btn-primary btn-sm" style={{ flexShrink: 0 }}>
                          <FaPlus size={11} /> Add
                        </button>
                      </div>
                      <div style={{ maxHeight: '56vh', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 8, paddingRight: 4 }}>
                        {roles.length === 0 && (
                          <div className="empty-state" style={{ padding: 'var(--sp-5) 0' }}>
                            <div className="empty-icon"><FaUserTag /></div>
                            <p>No roles yet. Add one above to get started.</p>
                          </div>
                        )}
                        {roles.map((role) => (
                          <button
                            key={role.id}
                            onClick={() => setActiveRoleId(role.id)}
                            className="btn btn-ghost"
                            style={{
                              width: '100%', justifyContent: 'flex-start', textAlign: 'left', fontWeight: 600,
                              borderColor: activeRoleId === role.id ? 'var(--accent)' : 'var(--border)',
                              background: activeRoleId === role.id ? 'rgba(124,58,237,.14)' : 'var(--surface-1)',
                              color: activeRoleId === role.id ? 'var(--text-1)' : 'var(--text-2)',
                            }}
                          >
                            {role.name}
                            {Number(role.is_default) === 1 ? <span className="pill pill-info" style={{ marginLeft: 8, fontSize: 10 }}>default</span> : null}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Path toggles */}
              <div className="lg:col-span-8">
                <div className="panel">
                  <div className="section-head" style={{ flexWrap: 'wrap', gap: 10 }}>
                    <div className="chip">
                      Active role: <b style={{ marginLeft: 4 }}>{activeRole?.name || "—"}</b>
                    </div>
                    <div className="filter-row" style={{ flexWrap: 'wrap' }}>
                      <div style={{ position: 'relative' }}>
                        <FaSearch size={12} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
                        <input
                          type="search"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder="Search path"
                          aria-label="Search path"
                          style={{ paddingLeft: 30, width: 180 }}
                        />
                      </div>
                      <button onClick={selectAll} className="btn btn-ghost btn-sm">Select all</button>
                      <button onClick={clearAll} className="btn btn-ghost btn-sm">Clear</button>
                      <button
                        onClick={saveRolePaths}
                        disabled={saving || activeRoleId == null}
                        className={saving ? "btn btn-sm btn-ghost" : "btn btn-sm btn-primary"}
                      >
                        <FaSave size={12} />{saving ? "Saving..." : "Save access"}
                      </button>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-3" style={{ padding: 'var(--sp-4)' }}>
                    {loading ? (
                      [...Array(6)].map((_, i) => (
                        <div key={i} className="skeleton" style={{ height: 64 }} />
                      ))
                    ) : filteredPaths.length === 0 ? (
                      <div className="empty-state" style={{ gridColumn: '1 / -1' }}>
                        <div className="empty-icon"><FaSearch /></div>
                        <h3>No paths match your search</h3>
                        <p>Try a different keyword, or clear the search box to see all paths.</p>
                      </div>
                    ) : (
                      filteredPaths.map(p => {
                        const enabled = selectedKeys.has(p.key);
                        return (
                          <motion.label
                            key={p.key}
                            whileTap={{ scale: 0.98 }}
                            className="panel"
                            style={{
                              cursor: 'pointer', padding: 'var(--sp-3)', display: 'flex', alignItems: 'flex-start', gap: 10,
                              borderColor: enabled ? 'var(--success)' : 'var(--border)',
                              background: enabled ? 'rgba(63,174,106,.1)' : 'var(--surface-2)',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={enabled}
                              onChange={() => toggleKey(p.key)}
                              style={{ marginTop: 2 }}
                              aria-label={`Toggle access to ${p.label}`}
                            />
                            <div style={{ fontSize: 12.5 }}>
                              <div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{p.label}</div>
                              <div style={{ color: 'var(--text-3)', fontSize: 11 }}>{p.route}</div>
                              <div style={{ color: 'var(--text-3)', fontSize: 10 }}>key: {p.key}</div>
                            </div>
                          </motion.label>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Toast */}
          {toast && (
            <div className="fixed bottom-4 right-4 z-50">
              <Toast type={toast.type} msg={toast.msg}/>
            </div>
          )}

          {/* Assign modal */}
          {assignOpen && (
            <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="assign-role-title">
              <div className="modal-panel panel">
                <div className="modal-head">
                  <h3 id="assign-role-title">Assign Role to User</h3>
                  <button onClick={() => setAssignOpen(false)} className="icon-btn" aria-label="Close">
                    <FaTimes size={14} />
                  </button>
                </div>
                <div className="modal-body">
                  <p className="field-hint" style={{ marginBottom: 'var(--sp-4)' }}>
                    Enter Email or SLIITLeoID and choose role.
                  </p>
                  <div className="field">
                    <label className="field-label" htmlFor="assign-email-or-id">Email or SLIITLeoID</label>
                    <input
                      id="assign-email-or-id"
                      type="text"
                      value={assignTarget.emailOrId}
                      onChange={e => setAssignTarget(t => ({ ...t, emailOrId: e.target.value }))}
                      placeholder="Email or SLIITLeoID"
                    />
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label className="field-label" htmlFor="assign-role-select">Role</label>
                    <select
                      id="assign-role-select"
                      value={assignTarget.roleId || ""}
                      onChange={e => setAssignTarget(t => ({ ...t, roleId: e.target.value }))}
                    >
                      <option value="">Select role…</option>
                      {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="modal-foot">
                  <button onClick={()=>setAssignOpen(false)} className="btn btn-ghost">Cancel</button>
                  <button onClick={assignRole} className="btn btn-primary">Assign</button>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}
